<script>
import Title from "@/components/admin/common/TitleArea.vue"

const eventTypes = [
  { label: '로그인 성공', value: 'auth.login' },
  { label: '로그인 실패', value: 'auth.login_fail' },
  { label: '로그아웃', value: 'auth.logout' },
  { label: '비밀번호 재설정', value: 'auth.password_reset_link_create' },
  { label: 'API 호출', value: 'api' },
  { label: '페이지 이동', value: 'page' },
  { label: '클라이언트 로그', value: 'client.log' }
]

export default {
  name: "Admin-user-behavior-tracking",
  meta: { title: "어드민-사용자 활동 로그" },
  components: { Title },
  data() {
    return {
      description: {
        title: '사용자 활동 로그',
        subTitle: '세션별 API 요청과 업무 활동을 조회합니다. 개인정보는 일부 마스킹됩니다.'
      },
      step: 1,
      loading: false,
      detailLoading: false,
      errorMessage: '',
      behaviorGroupList: [],
      selectedBehaviorGroup: {},
      selectedUserLogList: [],
      detailPagination: { page: 1, rowsPerPage: 50, rowsNumber: 0 },
      startDate: '',
      endDate: '',
      userId: '',
      partnerId: '',
      selectedEventTypes: [],
      eventTypeOptions: eventTypes,
      pagination: { page: 1, rowsPerPage: 50, rowsNumber: 0 },
      columns: [
        { name: 'user_id', label: '사용자 ID', align: 'center', field: 'user_id' },
        { name: 'user_name', label: '사용자명', align: 'center', field: 'user_name' },
        { name: 'partner_name', label: '파트너명', align: 'center', field: 'partner_name' },
        { name: 'event_count', label: '이벤트 수', align: 'center', field: 'event_count' },
        { name: 'ip_text', label: 'IP(마스킹)', align: 'center', field: 'ip_text' },
        { name: 'first_event_utc', label: '첫 활동 (Australia/Sydney)', align: 'center', field: 'first_event_utc', format: this.formatDateTime },
        { name: 'last_event_utc', label: '마지막 활동', align: 'center', field: 'last_event_utc', format: this.formatDateTime },
        { name: 'group_key', label: '활동 그룹', align: 'center', field: (row) => this.maskSession(row.group_key) }
      ],
      selectedUserLogColumns: [
        { name: 'ts_utc', label: '시간 (Australia/Sydney)', align: 'center', field: 'ts_utc', format: this.formatDateTime },
        { name: 'event_type', label: '이벤트', align: 'center', field: 'event_type' },
        { name: 'url_path', label: 'URL 경로', align: 'left', field: 'url_path' },
        { name: 'route_from', label: '이전 경로', align: 'center', field: 'route_from' },
        { name: 'route_to', label: '이후 경로', align: 'center', field: 'route_to' },
        { name: 'action', label: '액션', align: 'center', field: 'action' },
        { name: 'method', label: '메서드', align: 'center', field: 'method' },
        { name: 'status', label: '상태', align: 'center', field: 'status' },
        { name: 'ok', label: '결과', align: 'center', field: 'ok', format: (value) => Number(value) === 1 ? '성공' : '실패' },
        { name: 'payload', label: '정제된 payload', align: 'left', field: (row) => this.formatPayload(row.payload) },
        { name: 'user_agent', label: 'User-Agent', align: 'left', field: 'user_agent' }
      ]
    }
  },
  methods: {
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        timeZoneName: 'short'
      }).format(date)
    },
    maskSession(value) {
      if (!value) return '세션 없음'
      const text = String(value)
      return text.length <= 10 ? text : `${text.slice(0, 6)}…${text.slice(-4)}`
    },
    activityIp(row) {
      const count = Number(row?.ip_count || 0)
      if (count > 1) return `${row.ip_text || '-'} 외 ${count - 1}개`
      return row?.ip_text || '-'
    },
    formatPayload(value) {
      if (value == null) return '-'
      if (typeof value === 'string') return value
      try { return JSON.stringify(value) } catch (_) { return '[표시할 수 없음]' }
    },
    endExclusive(value) {
      if (!value) return undefined
      return new Date(new Date(`${value}T00:00:00.000Z`).getTime() + 86400000).toISOString()
    },
    buildParams() {
      const params = { page: this.pagination.page, page_size: this.pagination.rowsPerPage }
      if (this.startDate) params.start = `${this.startDate}T00:00:00.000Z`
      if (this.endDate) params.end = this.endExclusive(this.endDate)
      if (this.userId.trim()) params.user_id = this.userId.trim()
      if (this.partnerId.trim()) params.partner_id = this.partnerId.trim()
      if (this.selectedEventTypes.length) params.event_types = this.selectedEventTypes.join(',')
      return params
    },
    buildDetailScope() {
      const scope = { days: 7 }
      if (this.startDate) scope.start = `${this.startDate}T00:00:00.000Z`
      if (this.endDate) scope.end = this.endExclusive(this.endDate)
      if (this.selectedEventTypes.length) scope.event_types = this.selectedEventTypes.join(',')
      return scope
    },
    async fetchUserLogList() {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await this.$http({
          method: 'get', url: '/api/log/session-list',
          name: '[ Axios / 사용자 활동 로그 조회 ]', params: this.buildParams()
        })
        this.behaviorGroupList = res.data?.items || []
        this.pagination.rowsNumber = Number(res.data?.pagination?.total || 0)
        this.step = 1
      } catch (error) {
        this.behaviorGroupList = []
        this.pagination.rowsNumber = 0
        this.errorMessage = error?.response?.data?.message || '사용자 활동 로그를 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    async applyFilters() {
      this.pagination.page = 1
      await this.fetchUserLogList()
    },
    async resetFilters() {
      this.startDate = ''
      this.endDate = ''
      this.userId = ''
      this.partnerId = ''
      this.selectedEventTypes = []
      await this.applyFilters()
    },
    async onRequest({ pagination }) {
      this.pagination.page = pagination.page
      this.pagination.rowsPerPage = pagination.rowsPerPage
      await this.fetchUserLogList()
    },
    async fetchSelectedUserLogList(row) {
      const groupKey = row?.group_key
      if (!groupKey) return
      if (this.selectedBehaviorGroup.group_key !== row.group_key) {
        this.detailPagination.page = 1
      }
      this.selectedBehaviorGroup = row
      this.detailLoading = true
      this.errorMessage = ''
      try {
        const res = await this.$http({
          method: 'post', url: '/api/log/session-events',
          name: '[ Axios / 세션 활동 상세 조회 ]',
          data: {
            group_key: groupKey,
            page: this.detailPagination.page,
            page_size: this.detailPagination.rowsPerPage,
            ...this.buildDetailScope()
          }
        })
        this.selectedUserLogList = res.data?.items || []
        this.detailPagination.rowsNumber = Number(res.data?.pagination?.total || 0)
        this.step = 2
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || '세션 활동 상세를 불러오지 못했습니다.'
      } finally {
        this.detailLoading = false
      }
    },
    async onDetailRequest({ pagination }) {
      this.detailPagination.page = pagination.page
      this.detailPagination.rowsPerPage = pagination.rowsPerPage
      await this.fetchSelectedUserLogList(this.selectedBehaviorGroup)
    }
  },
  mounted() {
    this.fetchUserLogList()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg"><div class="col"><Title :desc="description" /></div></div>
    <div class="comp-box">
      <div class="text-body2 text-grey-7 q-mb-md">
        장애·운영 조사용 세션 활동입니다. 서버에 기록된 API·페이지·업무 이벤트를 페이지당 최대 100건씩 보여줍니다. 세션이 없는 활동은 목록만 확인할 수 있습니다.
      </div>
      <template v-if="step === 1">
        <div class="row q-col-gutter-md q-mb-md">
          <div class="col-12 col-sm-6 col-md-2"><q-input v-model="startDate" type="date" label="시작일" clearable dense outlined /></div>
          <div class="col-12 col-sm-6 col-md-2"><q-input v-model="endDate" type="date" label="종료일" clearable dense outlined /></div>
          <div class="col-12 col-sm-6 col-md-2"><q-input v-model="userId" label="사용자 ID" clearable dense outlined /></div>
          <div class="col-12 col-sm-6 col-md-2"><q-input v-model="partnerId" label="파트너 ID" clearable dense outlined /></div>
        </div>
        <div class="row q-col-gutter-md q-mb-md items-center">
          <div class="col-12 col-md-9">
            <q-select v-model="selectedEventTypes" label="이벤트 타입 (미선택 시 전체)" :options="eventTypeOptions" emit-value map-options multiple use-chips dense outlined />
          </div>
          <div class="col-12 col-md-3 row q-gutter-sm">
            <q-btn color="primary" label="조회" :loading="loading" @click="applyFilters" />
            <q-btn flat color="grey-8" label="초기화" :disable="loading" @click="resetFilters" />
          </div>
        </div>
      </template>
      <q-banner v-if="errorMessage" class="bg-red-1 text-negative q-mb-md" rounded>{{ errorMessage }}</q-banner>
      <template v-if="step === 1">
        <q-table
          :rows="behaviorGroupList"
          :columns="columns"
          row-key="row_key"
          :loading="loading"
          v-model:pagination="pagination"
          :rows-per-page-options="[25, 50, 100]"
          no-data-label="조건에 맞는 사용자 활동이 없습니다."
          @request="onRequest"
          @row-click="(event, row) => row.group_key && fetchSelectedUserLogList(row)" />
      </template>
      <template v-else>
        <div class="row q-mb-md items-center q-gutter-sm">
          <q-btn icon="chevron_left" color="primary" label="목록으로" @click="step = 1" />
          <span class="text-grey-7">활동 그룹 {{ maskSession(selectedBehaviorGroup.group_key) }} · 전체 {{ detailPagination.rowsNumber }}건</span>
        </div>
        <div class="row q-col-gutter-md q-mb-md text-body2">
          <div class="col-12 col-md-3"><strong>사용자:</strong> {{ selectedBehaviorGroup.user_name || selectedBehaviorGroup.user_id || '-' }}</div>
          <div class="col-12 col-md-3"><strong>파트너:</strong> {{ selectedBehaviorGroup.partner_name || selectedBehaviorGroup.partner_id || '-' }}</div>
          <div class="col-12 col-md-3"><strong>IP:</strong> {{ activityIp(selectedBehaviorGroup) }}</div>
          <div class="col-12 col-md-3"><strong>이벤트:</strong> {{ selectedBehaviorGroup.event_count || 0 }}건</div>
        </div>
        <q-table
          :rows="selectedUserLogList"
          :columns="selectedUserLogColumns"
          row-key="id"
          :loading="detailLoading"
          v-model:pagination="detailPagination"
          :rows-per-page-options="[25, 50, 100]"
          no-data-label="세션 상세 활동이 없습니다."
          @request="onDetailRequest" />
      </template>
    </div>
  </q-page>
</template>

<style scoped></style>
